/**
 * Minimal `{{ path.to.value }}` substitution against a flat scope.
 *
 * Used by the block renderer so operator-authored `markdown` and
 * `paragraph` blocks can interpolate runtime values from the flow
 * scope (`scope.weatherTip`, `scope.integration.avits-lookup.applicant_name`,
 * etc.). Intentionally a tiny implementation: no conditionals, no loops,
 * no escaping — operators sanitize their downstream concerns via the
 * markdown allowlist or the JSX text node. Whitespace inside the
 * braces is tolerated. Missing paths render as the empty string.
 *
 * Mirrors `applyTemplate` in `@upg/services-api/src/integrations/webhook-validator.ts`
 * so server-side validator templates and client-side block templates
 * use the same surface area (operator learns one syntax).
 *
 * Operator convention is `{{ scope.<key>.<sub> }}`; the runner passes the
 * `FlowScope` object directly (the object whose keys are `validator_inputs`,
 * `integration`, `tenant_id`, …). A leading `scope.` segment is therefore
 * stripped before walking — it is operator syntax, not a real key. The
 * older bare-path form (`{{ foo.bar }}`) still works against the same root
 * so templates authored before this convention keep rendering.
 */
export function applyScopeTemplate(
  input: string,
  scope: Record<string, unknown>,
  opts?: {
    /**
     * Transform applied to each substituted value (NOT the surrounding
     * operator template). When the output is HTML — e.g. markdown that will be
     * parsed and mounted — pass {@link escapeHtml} so untrusted runtime values
     * (AVITS fields, shopper input, LLM output) are treated as inert data and
     * cannot inject markup (bd 6oc). Omitted = raw substitution (plain-text
     * contexts).
     */
    escapeValue?: (raw: string) => string;
  },
): string {
  if (!input) return '';
  return input.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, key: string) => {
    const path = key.startsWith('scope.') ? key.slice('scope.'.length) : key;
    const value = readDotPath(scope, path);
    if (value === null || value === undefined) return '';
    const s = String(value);
    return opts?.escapeValue ? opts.escapeValue(s) : s;
  });
}

/**
 * HTML-entity-encode the five significant characters so a string is rendered
 * as literal text when placed into an HTML context. Used to neutralize
 * untrusted template values before markdown parsing.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function readDotPath(obj: unknown, path: string): unknown {
  if (!path) return undefined;
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}
